import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EbbUtilityService } from '@app/browser-extension/services/ebb-utility.service';
import { isEmpty, pascalCaseKeys } from '@degreed/core/utils';
import {
  NgxHttpClient,
  TrackerService,
  WindowToken,
} from '@degreed/lxp/services';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, tap } from 'rxjs/operators';
import { UserData } from '../models/user-data.model';
import { EbbAuthUserService } from './ebb-auth-user.service';
import { EbbConfigService } from './ebb-config.service';

@Injectable({
  providedIn: 'root',
})
export class EbbAuthService {
  private loading$ = new BehaviorSubject<boolean>(true);
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  private authCheckResponse = new BehaviorSubject<false | UserData>(false);

  private accessToken: string;
  private forceEbbLogout: boolean = false;
  public readonly isLoggedIn$ = this.isLoggedInSubject.asObservable();

  constructor(
    private http: NgxHttpClient,
    private config: EbbConfigService,
    private router: Router,
    private tracker: TrackerService,
    private cookieService: CookieService,
    private authUserService: EbbAuthUserService,
    private utilityService: EbbUtilityService,
    @Inject(WindowToken) private windowRef: Window
  ) {
    this.authUserService.userData
      .pipe(filter((userData) => !!userData))
      .subscribe((userData: any) => {
        if (userData.AnalyticsEnabled) {
          this.utilityService.setAnalytics();
        }
        this.tracker.trackEventData({
          action: 'Extension Viewed',
        });
      });
    this.checkIfShouldForceLogout();
  }

  private checkIfShouldForceLogout() {
    this.forceEbbLogout = localStorage.getItem('forceEbbLogout') === 'true';
  }

  public get loading(): Observable<boolean> {
    return this.loading$.asObservable();
  }

  public get isLoggedIn(): boolean {
    return this.isLoggedInSubject.value;
  }

  public set isLoggedIn(value: boolean) {
    this.isLoggedInSubject.next(value);
  }

  public get authUserLocale(): string {
    return (this.authCheckResponse.value as any)?.authedUser?.viewerProfile
      ?.localeId;
  }

  public logOut(): Observable<void> {
    return this.logOff().pipe(
      finalize(() => {
        this.clearStorage();
        this.clearAuthStatus();
        if (typeof chrome !== 'undefined' && chrome.runtime) {
          // Available and non-interceptable: manifest's externally_connectable
          chrome.runtime.sendMessage(this.config.EXTENSION_ID, {
            message: this.config.EVENT_CLEAR_SERVICE_HOST,
          });
        }
        this.router.navigate(['/degreed-button/app/auth'], {
          queryParams: { forceNewLogin: true },
          replaceUrl: true,
        });
        this.tracker.trackEventData({
          action: 'Extension Logged Out',
        });
      })
    );
  }

  public initMessageListener(): void {
    const onMessage = (e) => {
      let data, stateData, responseData;

      if (!e.data) {
        this.router.navigate(['/degreed-button/app/auth'], {
          replaceUrl: true,
        });
        return;
      }

      data = e.data;

      if (typeof e.data === 'string') {
        try {
          data = JSON.parse(e.data);
        } catch (e) {
          // if this fails, keep data as-is
        }
      }

      if (data.name === 'unauthorized') {
        this.logOff().subscribe(() => {
          stateData = {
            msg: data.data, // url
            status: 401,
            data: undefined,
          };
          this.router.navigate(['/degreed-button/app/auth'], {
            queryParams: { stateData },
            replaceUrl: true,
          });
        });
      } else if (data.name === this.config.EVENT_CHECK_AUTH) {
        responseData = data.data ? data.data : data;
        this.onSuccess(responseData);
      }
    };

    this.windowRef.addEventListener('message', (e) => {
      onMessage(e);
    });
  }

  public authCheckWithCommonHandlers(): Observable<boolean> {
    return this.authCheck().pipe(
      map((response: UserData | false) => {
        if (response === false) {
          this.onError(response);
          this.loading$.next(false);
          return this.isLoggedIn;
        }
        response = pascalCaseKeys(response);
        this.onSuccess(response);
        this.loading$.next(false);
        return this.isLoggedIn;
      })
    );
  }

  public setAccessToken(value): void {
    // Store only in-memory and extension storage (NOT localStorage)
    this.accessToken = value;

    if (value && typeof chrome !== 'undefined' && chrome.runtime) {
      // Available and non-interceptable: manifest's externally_connectable
      chrome.runtime.sendMessage(this.config.EXTENSION_ID, {
        message: this.config.EVENT_SET_ACCESS_TOKEN,
        token: value,
      });
    }
  }

  public setLoading(status: boolean) {
    this.loading$.next(status);
  }

  public getAccessToken(): string {
    // Return only in-memory token (no localStorage fallback)
    return this.accessToken;
  }

  // The promise nature of this call isn't required by our code anymore,
  // but it continues to work as-so and is more future-proof.
  public getAccessTokenAsync(): Promise<string> {
    // Don't read from localStorage - only query extension storage
    return new Promise((resolve) => {
      if (typeof chrome !== 'undefined' && chrome.runtime) {
        // Available and non-interceptable: manifest's externally_connectable
        chrome.runtime.sendMessage(
          this.config.EXTENSION_ID,
          {
            message: this.config.EVENT_GET_ACCESS_TOKEN,
          },
          (response) => {
            if (isEmpty(response)) {
              if (this.accessToken) {
                // sync in-memory token to extension storage
                // Available and non-interceptable: manifest's externally_connectable
                chrome.runtime.sendMessage(this.config.EXTENSION_ID, {
                  message: this.config.EVENT_SET_ACCESS_TOKEN,
                  token: this.accessToken,
                });
                resolve(this.accessToken);
                this.setAccessToken(this.accessToken);
              } else {
                resolve('');
                this.setAccessToken(null);
              }
            } else if (response) {
              resolve(response);
              this.setAccessToken(response);
            } else {
              resolve('');
              this.setAccessToken(null);
            }
          }
        );
      } else {
        resolve(this.accessToken);
        this.setAccessToken(this.accessToken);
      }
    });
  }

  public clearStorage(): void {
    this.clearToken();
    this.clearHostInfo();
    this.authUserService.isLoggedIn = false;
    this.isLoggedIn = false;
    localStorage.removeItem('forceEbbLogout');
  }

  public authCheck(): Observable<UserData | boolean> {
    this.checkIfShouldForceLogout();
    if (this.forceEbbLogout) {
      this.logOut();
      return of(false);
    } else {
      if (this.authCheckResponse.value) {
        return of(this.authCheckResponse.value);
      }
      return this.http
        .get<UserData>('/extension/util/authcheck', {
          headers: {
            // Ensure that this call is ignored by the auth.intercept service
            'Dg-Skip-Intercept': 'true',
          },
        })
        .pipe(
          catchError((_) => of(false)),
          tap((response: false | UserData) =>
            this.authCheckResponse.next(response)
          )
        );
    }
  }

  public logOff(): Observable<void> {
    return this.http.post('/extension/util/account/logoff', {});
  }

  public eventToPromise<T = unknown, D = Record<string, string>>(
    event,
    data: D
  ): Promise<T> {
    return new Promise((resolve) => {
      chrome.runtime.sendMessage(
        this.config.EXTENSION_ID,
        { ...data, message: event },
        resolve
      );
    });
  }

  public async syncServiceHostIfNotExists(): Promise<void> {
    const host = await this.eventToPromise<string>(
      this.config.EVENT_GET_SERVICE_HOST,
      {}
    );

    // Checks if service host is removed from extension storage and user is trying to
    // log back in without refreshing the page -> not giving the email as in input
    // So, we set the service host as the current host url
    if (
      isEmpty(host) &&
      this.windowRef.location.origin.startsWith('https://')
    ) {
      await this.eventToPromise(this.config.EVENT_SET_SERVICE_HOST, {
        value: this.windowRef.location.origin,
      });
    }
  }

  private storeAuthStatus(): void {
    localStorage.setItem('isLoggedIn', new Date().toJSON());
    // reset the ngx client-side cache busting token
    localStorage.removeItem('authUser:v');
  }

  private clearAuthStatus(): void {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('authUser:v');
  }

  private clearToken(): void {
    // Clear in-memory token
    this.accessToken = null;

    // Clear extension storage
    if (typeof chrome !== 'undefined' && chrome.runtime) {
      chrome.runtime.sendMessage(this.config.EXTENSION_ID, {
        message: this.config.EVENT_CLEAR_ACCESS_TOKEN,
      });
    }

    // Clean up any legacy storage (remove after migration period)
    localStorage.removeItem(this.config.TOKEN_KEY);
    this.cookieService.delete(this.config.TOKEN_KEY);
  }

  private clearHostInfo(): void {
    localStorage.removeItem(this.config.HOST_KEY);
    this.cookieService.delete(this.config.HOST_KEY);
  }

  private onSuccess(response): void {
    this.checkIfShouldForceLogout();
    if (this.forceEbbLogout) {
      localStorage.removeItem('forceEbbLogout');
      this.logOut();
    } else {
      this.isLoggedIn = !!response;
      this.storeAuthStatus();
      this.authUserService.setUserData(response);
    }
  }

  private onError(err): void {
    this.clearStorage();
  }
}
